import { useCallback, useState, type ReactNode } from "react";
import { FaRegTrashCan } from "react-icons/fa6";

import { Button } from "shared/ui/button";
import { ConfirmDialog } from "shared/ui/confirm-dialog";
import { Sheet } from "shared/ui/sheet";

type Props = {
  value: string | undefined;
  title: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onChange: (value: string) => void;
  onDelete?: () => void;
};

const placeholder =
  "For example: This is my personal account. Use it for scheduling personal appointments (doctor, dentist, haircuts), managing personal events and birthdays, communicating with friends and family, storing personal files and photos";

export const AddAccountInstructionsSheet = (props: Props) => {
  const { isOpen, title, onClose, onDelete, value, onChange } = props;
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [inputValue, setInputValue] = useState(value || "");

  const handleSave = () => {
    onChange(inputValue);
    onClose();
  };
  const handleTrashClick = () => {
    setIsConfirmOpen(true);
  };

  const refCb = useCallback((node: HTMLTextAreaElement | null) => {
    if (!node) return;
    node.focus();
  }, []);

  return (
    <>
      <Sheet isOpen={isOpen} onClose={onClose}>
        <Sheet.Header onClose={onClose}>
          <div className="flex grow items-center gap-3">
            <span className="mr-auto inline-flex">{title}</span>
            {onDelete && (
              <Button
                theme="danger"
                size="34"
                rounded
                className="bg-pale-red/15"
                onClick={handleTrashClick}
              >
                <FaRegTrashCan className="text-pale-red size-5" />
              </Button>
            )}
            <Button size="34" onClick={handleSave}>
              Save
            </Button>
          </div>
        </Sheet.Header>

        <Sheet.Content>
          <textarea
            ref={refCb}
            className="w-full rounded-xl p-4 outline-hidden transition-colors focus:bg-white/5"
            value={inputValue}
            rows={5}
            onChange={(e) => {
              const value = e.target.value;
              setInputValue(value);
            }}
            placeholder={placeholder}
          />
        </Sheet.Content>
      </Sheet>

      {isConfirmOpen && onDelete && (
        <ConfirmDialog
          title="Remove Account"
          description="Are you sure you want to remove this account from Resonar AI?"
          cancelText="Cancel"
          confirmText="Remove from Resonar AI"
          onCancel={() => setIsConfirmOpen(false)}
          onConfirm={onDelete}
          isOpen={isConfirmOpen}
          onOpenChange={setIsConfirmOpen}
        />
      )}
    </>
  );
};
